import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Trash2, Eye, Calendar, Building, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDialog, setViewDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/messages`, { withCredentials: true });
      setMessages(data);
    } catch (e) {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleView = async (message) => {
    setSelectedMessage(message);
    setViewDialog(true);
    
    if (!message.is_read) {
      try {
        await axios.put(`${API}/api/admin/messages/${message.id}/read`, {}, { withCredentials: true });
        fetchMessages();
      } catch (e) {
        console.error('Error marking as read:', e);
      }
    }
  };

  const handleDelete = (message) => {
    setSelectedMessage(message);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/admin/messages/${selectedMessage.id}`, { withCredentials: true });
      toast.success('Message supprimé');
      fetchMessages();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialog(false);
      setSelectedMessage(null);
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white h-20 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-messages">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-1">Messages</h1>
          <p className="text-slate-600">
            {unreadCount > 0 ? `${unreadCount} message(s) non lu(s)` : 'Tous les messages sont lus'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">{messages.length} message(s)</span>
        </div>
      </div>

      {/* Messages List */}
      <div className="bg-white border border-slate-200">
        <div className="divide-y divide-slate-100">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`p-4 sm:p-6 flex items-start gap-3 sm:gap-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                !message.is_read ? 'bg-cyan-50/50' : ''
              }`}
              onClick={() => handleView(message)}
              data-testid={`message-row-${message.id}`}
            >
              <div className={`w-3 h-3 rounded-full mt-2 flex-shrink-0 ${
                message.is_read ? 'bg-slate-300' : 'bg-cyan-500'
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className={`font-medium ${message.is_read ? 'text-slate-700' : 'text-slate-900'}`}>
                    {message.name}
                  </h3>
                  <span className="text-slate-400 text-xs flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(message.created_at).toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <p className="text-slate-600 text-sm font-medium mb-1">{message.subject}</p>
                <p className="text-slate-500 text-sm truncate">{message.message}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 hidden sm:flex">
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleView(message); }}>
                  <Eye className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-rose-600"
                  onClick={(e) => { e.stopPropagation(); handleDelete(message); }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {messages.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              <Mail className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun message reçu</p>
            </div>
          )}
        </div>
      </div>

      {/* View Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Message de {selectedMessage?.name}</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="py-4 space-y-6">
              {/* Sender Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-sm">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <a href={`mailto:${selectedMessage.email}`} className="text-cyan-600 hover:underline">
                      {selectedMessage.email}
                    </a>
                  </div>
                </div>
                {selectedMessage.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Téléphone</p>
                      <a href={`tel:${selectedMessage.phone}`} className="text-slate-900">
                        {selectedMessage.phone}
                      </a>
                    </div>
                  </div>
                )}
                {selectedMessage.company && (
                  <div className="flex items-center gap-3">
                    <Building className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Société</p>
                      <p className="text-slate-900">{selectedMessage.company}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Date</p>
                    <p className="text-slate-900">
                      {new Date(selectedMessage.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div>
                <p className="text-xs text-slate-500 mb-1">Sujet</p>
                <p className="font-heading font-bold text-lg text-slate-900">{selectedMessage.subject}</p>
              </div>

              {/* Message */}
              <div>
                <p className="text-xs text-slate-500 mb-1">Message</p>
                <div className="bg-white border border-slate-200 p-4 whitespace-pre-wrap text-slate-700">
                  {selectedMessage.message}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => { setViewDialog(false); handleDelete(selectedMessage); }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </Button>
                <Button asChild className="bg-navy hover:bg-navy/90 text-white">
                  <a href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}>
                    <Mail className="h-4 w-4 mr-2" />
                    Répondre par email
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le message de "{selectedMessage?.name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminMessages;
